import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChoiceBlock } from '@/components/features/chat-messages/choice-block';
import { useFlashCardRun } from '@/components/features/chat-messages/flash-card-run-context';
import type { FlashCardSessionData } from '@/components/layout/chat-pane.types';
import { testIds } from '@/lib/test-ids';
import { submitQuizPracticeSession } from '@/lib/api/quiz';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Pause, Play, X } from 'lucide-react';

type CardAnswer = {
  choice: string;
  correct: boolean;
  ms: number;
};

type FlashCardWidgetProps = {
  session: FlashCardSessionData;
  onComplete?: (summary: {
    correctCount: number;
    total: number;
    weakDomains: string[];
  }) => void;
  /** Persist index, answers, and done onto the assistant message. */
  onPersist?: (patch: {
    progress: { index: number; total: number };
    savedAnswers: Record<string, { choice: string; correct: boolean; ms?: number }>;
    done: boolean;
  }) => void;
};

const CARD_TRANSITION_MS = 220;

/** In-chat flash card study loop (quiz-grade policy on ChoiceBlock). */
export function FlashCardWidget({ session, onComplete, onPersist }: FlashCardWidgetProps) {
  const cards = session.cards?.length ? session.cards : [session.card];
  const [index, setIndex] = useState(Math.min(session.progress?.index || 0, Math.max(cards.length - 1, 0)));
  const [answers, setAnswers] = useState<Record<string, CardAnswer>>(() => {
    const saved = session.savedAnswers || {};
    const out: Record<string, CardAnswer> = {};
    for (const [id, row] of Object.entries(saved)) {
      out[id] = { choice: row.choice, correct: row.correct, ms: row.ms ?? 0 };
    }
    return out;
  });
  const [cardStartedAt] = useState(() => Date.now());
  const [done, setDone] = useState(Boolean(session.done));
  const [summary, setSummary] = useState<{
    correctCount: number;
    total: number;
    weakDomains: string[];
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [cardPhase, setCardPhase] = useState<'in' | 'out'>('in');
  const rootRef = useRef<HTMLDivElement>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    state: runState,
    register,
    updateProgress,
    unregister,
    resume,
    pauseOnUserChat,
    dismiss,
  } = useFlashCardRun();
  const isPaused = runState.paused && runState.sessionId === session.sessionId;
  const isDismissed = runState.dismissedSessionId === session.sessionId;

  const card = cards[index];
  const progressLabel = `Card ${index + 1} / ${cards.length}${
    card?.domain ? ` - ${card.domain.replace(/_/g, ' ')}` : ''
  }`;

  const options = useMemo(
    () =>
      Object.entries(card?.options || {}).map(([id, label]) => ({
        id,
        label,
      })),
    [card]
  );

  const pinToStreamTop = useCallback(() => {
    requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }, []);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'nearest' });
    // Once, when this session first appears. Later cards stay where the user scrolled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionId]);

  // Register incomplete run so chat send can pause. No effect-cleanup unregister
  // (that wiped pause/CTA between re-registers). Unregister on done/dismiss/unmount only.
  useEffect(() => {
    if (done || isDismissed) {
      unregister(session.sessionId);
      return;
    }
    register({
      sessionId: session.sessionId,
      slug: session.slug || '',
      progressLabel,
      focus: pinToStreamTop,
    });
    // progressLabel via updateProgress below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session.sessionId,
    session.slug,
    done,
    isDismissed,
    register,
    unregister,
    pinToStreamTop,
  ]);

  useEffect(() => {
    const id = session.sessionId;
    return () => {
      unregister(id);
    };
  }, [session.sessionId, unregister]);

  useEffect(() => {
    if (done || isDismissed) return;
    updateProgress(session.sessionId, progressLabel);
  }, [progressLabel, session.sessionId, done, isDismissed, updateProgress]);

  useEffect(() => {
    onPersist?.({
      progress: { index, total: cards.length },
      savedAnswers: answers,
      done,
    });
  }, [index, answers, done, cards.length, onPersist]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, []);

  const finish = useCallback(
    async (finalAnswers: Record<string, CardAnswer>) => {
      const total = cards.length;
      const correctCount = Object.values(finalAnswers).filter((a) => a.correct).length;
      const domainTotals: Record<string, number> = {};
      const domainCorrect: Record<string, number> = {};
      for (const c of cards) {
        const d = c.domain || 'general';
        domainTotals[d] = (domainTotals[d] || 0) + 1;
        if (finalAnswers[c.id]?.correct) {
          domainCorrect[d] = (domainCorrect[d] || 0) + 1;
        }
      }
      const weakDomains = Object.keys(domainTotals).filter((d) => {
        const pct = ((domainCorrect[d] || 0) / domainTotals[d]) * 100;
        return pct < 70;
      });

      const result = { correctCount, total, weakDomains };
      setSummary(result);
      setDone(true);
      unregister(session.sessionId);
      onComplete?.(result);

      if (!session.slug) return;
      setSaving(true);
      try {
        const payloadAnswers: Record<string, { choice: string; correct: boolean; ms?: number }> =
          {};
        for (const [id, a] of Object.entries(finalAnswers)) {
          payloadAnswers[id] = { choice: a.choice, correct: a.correct, ms: a.ms };
        }
        await submitQuizPracticeSession(session.slug, {
          answers: payloadAnswers,
          source: 'chat',
          started_at: new Date(cardStartedAt).toISOString(),
          completed_at: new Date().toISOString(),
          selection: {
            question_ids: cards.map((c) => c.id),
            mode: 'practice',
          },
        });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to save practice session');
      } finally {
        setSaving(false);
      }
    },
    [cards, session.slug, session.sessionId, cardStartedAt, onComplete, pinToStreamTop, unregister]
  );

  const handleActivate = useCallback(
    (optionId: string) => {
      if (!card || isPaused) return;
      const correct = optionId.toLowerCase() === String(card.answer).toLowerCase();
      setAnswers((prev) => ({
        ...prev,
        [card.id]: {
          choice: optionId,
          correct,
          ms: Date.now() - cardStartedAt,
        },
      }));
    },
    [card, cardStartedAt, isPaused]
  );

  const handleNext = useCallback(() => {
    if (isPaused) return;
    if (index < cards.length - 1) {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      setCardPhase('out');
      transitionTimerRef.current = setTimeout(() => {
        setIndex((i) => i + 1);
        requestAnimationFrame(() => {
          setCardPhase('in');
        });
      }, CARD_TRANSITION_MS);
      return;
    }
    void finish(answers);
  }, [index, cards.length, answers, finish, isPaused]);

  if (!card) {
    return (
      <p className="text-sm text-muted-foreground p-2">No cards in this session.</p>
    );
  }

  if (isDismissed && !done) {
    return (
      <div
        ref={rootRef}
        className="mt-3 rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground"
        data-testid={testIds.flashCardWidget.dismissed}
      >
        Flash card session dismissed. Ask to start a new sprint when ready.
      </div>
    );
  }

  if (done && summary) {
    return (
      <div
        ref={rootRef}
        className="mt-3 rounded-lg border border-border bg-card p-4 sm:p-5 space-y-2 text-sm shadow-sm"
        data-testid={testIds.flashCardWidget.summary}
      >
        <p className="font-medium text-base">
          Practice complete: {summary.correctCount}/{summary.total} correct
          {saving ? ' (saving...)' : ''}
        </p>
        {summary.weakDomains.length > 0 ? (
          <p className="text-muted-foreground">
            Weak domains: {summary.weakDomains.join(', ')}
          </p>
        ) : (
          <p className="text-muted-foreground">No weak domains flagged.</p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="mt-3 -mx-1 sm:mx-0 scroll-mt-3"
      data-testid={testIds.flashCardWidget.root}
    >
      {isPaused ? (
        <div
          className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm"
          data-testid={testIds.flashCardWidget.pausedBanner}
        >
          <p className="min-w-0 flex-1 text-foreground">
            Paused for discussion - resume when ready
          </p>
          <Button
            type="button"
            size="sm"
            className="h-8 shrink-0"
            data-testid={testIds.flashCardWidget.resumeInline}
            onClick={resume}
          >
            Resume
          </Button>
        </div>
      ) : null}
      <div
        className={cn(
          'relative rounded-lg border border-border/80 bg-card shadow-sm ring-1 ring-border/40',
          'transition-[opacity,transform] ease-out will-change-[opacity,transform]',
          cardPhase === 'out'
            ? 'opacity-0 translate-y-3'
            : 'opacity-100 translate-y-0'
        )}
        style={{ transitionDuration: `${CARD_TRANSITION_MS}ms` }}
      >
        <div className="flex items-start justify-between gap-2 px-4 pt-3 sm:px-5">
          <p
            className="min-w-0 flex-1 text-sm font-medium text-muted-foreground tabular-nums pt-0.5"
            data-testid={testIds.choiceBlock.progress}
          >
            {progressLabel}
          </p>
          <div className="flex shrink-0 items-center gap-0.5 -mr-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label={isPaused ? 'Resume flash cards' : 'Pause flash cards'}
              data-testid={testIds.flashCardWidget.pause}
              onClick={isPaused ? resume : pauseOnUserChat}
            >
              {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label="Dismiss flash cards"
              data-testid={testIds.flashCardWidget.dismissCard}
              onClick={dismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className={cn(isPaused && 'opacity-60 pointer-events-none')}>
          <ChoiceBlock
            key={card.id}
            policy="quiz-grade"
            density="comfortable"
            disabled={isPaused}
            className="mt-0 border-0 bg-transparent shadow-none ring-0 rounded-none pt-1"
            question={card.stem}
            options={options}
            correctIds={[String(card.answer).toLowerCase()]}
            explanation={card.explanation}
            onActivate={handleActivate}
            onRevealed={pinToStreamTop}
            onNext={handleNext}
            nextLabel={index < cards.length - 1 ? 'Next' : 'Finish'}
          />
        </div>
      </div>
    </div>
  );
}
